import {
  FunnelPostfixOnColumnName,
  IChartSettings,
  IParsedChartDataFunnel,
  IParsedChartDataRecord,
} from '../interfaces';
import {
  FunnelModeKey,
  IMeasureControl,
} from '../../../utils/apollo/graphqlSchema';
import { DataKey } from 'recharts/types/util/types';
import { isNil } from 'lodash';
import { IChartLine } from '../hooks/useChartLines';
import { getValueFromDataPoint } from '../../../utils/redux/util';
import { UnreachableCaseError } from 'ts-essentials';

export interface IFunnelDataKeys {
  // absolute number of converted subjects at the step
  valueKey: DataKey<IParsedChartDataRecord>;
  // absolute number of bounced subjects at the step
  bounceValueKey?: DataKey<IParsedChartDataRecord>;

  // percents compared to the first step (i.e. absolute)
  // number of converted subjects compared to the first step (absolute percent)
  percentKey: DataKey<IParsedChartDataRecord>;
  // number of bounced subjects compared to the first step (absolute percent)
  bouncePercentKey?: DataKey<IParsedChartDataRecord>;

  // percents compared to the previous visible step (i.e. relative)
  relativePercentKey?: DataKey<IParsedChartDataRecord>;
  // relative bounce value compared to the previous visible step
  relativeBounceKey?: DataKey<IParsedChartDataRecord>;
  // relative bounce compared to the previous visible step
  relativeBouncePercentKey?: DataKey<IParsedChartDataRecord>;
  // same as relative, but the value scaled to fit to the previous visible step. Required to draw relative
  // values in an absolute scale chart
  // bounce value compared to the previous visible step
  relativeScaledBounceKey?: DataKey<IParsedChartDataRecord>;
  // absolute bounce compared to the previous visible step
  relativeScaledBouncePercentKey?: DataKey<IParsedChartDataRecord>;

  // for example if you have:
  // step 1: 100% (0% bounce)
  // step 2: 70% (30% bounce compared to step 1)
  // step 3: 35% (65% bounce compared to step 1, 50% conversion compared to step 2, 50% bounce compared to step 2)
}

export type IFunnelDataKeysPerLine = Record<string, IFunnelDataKeys>;

export enum MeasureMode {
  VISIBLE = 1,
  ALL = 2,
}

const formatKey = (
  columnKey: string,
  postfix: FunnelPostfixOnColumnName,
  measureKey: string,
) => columnKey + postfix + ':' + measureKey;

export const generateFunnelDataKeysPerLine = (
  lineKey: IChartLine['name'],
  prevLineKey: string | undefined,
  firstStepKey: string,
  prevStepKey: string | undefined,
): IFunnelDataKeys => {
  const percentKey = formatKey(
    lineKey,
    FunnelPostfixOnColumnName.percentKey,
    firstStepKey,
  );
  const bouncePercentKey = formatKey(
    lineKey,
    FunnelPostfixOnColumnName.bouncePercentKey,
    firstStepKey,
  );
  const bounceValueKey = formatKey(
    lineKey,
    FunnelPostfixOnColumnName.bounceKey,
    firstStepKey,
  );

  const relativePercentKey = prevStepKey
    ? formatKey(lineKey, FunnelPostfixOnColumnName.percentKey, prevStepKey)
    : percentKey;

  const relativeBouncePercentKey = prevStepKey
    ? formatKey(
        lineKey,
        FunnelPostfixOnColumnName.bouncePercentKey,
        prevStepKey,
      )
    : undefined;

  const relativeBounceKey = prevStepKey
    ? formatKey(lineKey, FunnelPostfixOnColumnName.bounceKey, prevStepKey)
    : undefined;

  const prevStepBouncePercentKey = prevLineKey
    ? formatKey(
        prevLineKey,
        FunnelPostfixOnColumnName.bouncePercentKey,
        firstStepKey,
      )
    : undefined;

  const prevStepBounceKey = prevLineKey
    ? formatKey(prevLineKey, FunnelPostfixOnColumnName.bounceKey, firstStepKey)
    : undefined;

  const relativeScaledBouncePercentKey = prevStepBouncePercentKey
    ? (dataForTheDay: IParsedChartDataRecord) => {
        return (
          (getValueFromDataPoint(dataForTheDay, bouncePercentKey) ?? 0) -
          (getValueFromDataPoint(dataForTheDay, prevStepBouncePercentKey) ?? 0)
        );
      }
    : undefined;

  const relativeScaledBounceKey = prevStepBounceKey
    ? (dataForTheDay: IParsedChartDataRecord) => {
        return (
          (getValueFromDataPoint(dataForTheDay, bounceValueKey) ?? 0) -
          (getValueFromDataPoint(dataForTheDay, prevStepBounceKey) ?? 0)
        );
      }
    : undefined;

  return {
    valueKey: lineKey,
    percentKey,
    bounceValueKey,
    bouncePercentKey,
    relativeScaledBounceKey,
    relativeScaledBouncePercentKey,
    relativePercentKey,
    relativeBounceKey,
    relativeBouncePercentKey,
  };
};

const getFunnelKeysAllDays = (
  visibleLines: IChartLine[],
  chartData: IParsedChartDataFunnel,
  measures: IMeasureControl[],
) => {
  const result: IFunnelDataKeysPerLine = {};

  for (const visibleLine of visibleLines) {
    const lineKey = visibleLine.name;

    const columnMeasureKey = chartData.linesMeta[lineKey].measureKey;
    const columnSliceKey = chartData.linesMeta[lineKey].sliceKey;

    const firstStep = measures[0];
    const firstStepKey = firstStep.key;

    let prevStep: typeof firstStep | undefined;
    const currentIndex = measures.findIndex(
      (vm) => vm.key === columnMeasureKey,
    );
    if (currentIndex > 0) {
      prevStep = measures[currentIndex - 1];
    }
    const prevStepKey = prevStep?.key;

    let prevLineKey: string | undefined;
    if (!isNil(prevStepKey)) {
      prevLineKey = chartData.columns.find(
        (c) => c.measureKey === prevStepKey && c.sliceKey === columnSliceKey,
      )?.key;
    }

    result[lineKey] = generateFunnelDataKeysPerLine(
      lineKey,
      prevLineKey,
      firstStepKey,
      prevStepKey,
    );
  }

  return result;
};

const comparativeDataKeys = (
  chartData: IParsedChartDataFunnel,
  chartSettings: IChartSettings,
  visibleLines: IChartLine[],
  measureMode: MeasureMode,
) => {
  let measures: IMeasureControl[];
  switch (measureMode) {
    case MeasureMode.VISIBLE:
      measures = chartData.measureControls.filter((mc) =>
        chartSettings.displayedMeasureSettings.find(
          (dms) => mc.key === dms.key && dms.isVisible,
        ),
      );
      break;
    case MeasureMode.ALL:
      measures = chartData.measureControls;
      break;
    default:
      throw new UnreachableCaseError(measureMode);
  }

  if (measures.length === 0) {
    return {};
  }

  return getFunnelKeysAllDays(visibleLines, chartData, measures);
};

const staticDataKeys = (visibleLines: IChartLine[]) =>
  visibleLines.reduce<IFunnelDataKeysPerLine>((acc, vl) => {
    acc[vl.name] = {
      valueKey: vl.name,
      percentKey: vl.name + FunnelPostfixOnColumnName.percentKey,
      relativePercentKey: vl.name + FunnelPostfixOnColumnName.percentKey,
    };
    return acc;
  }, {});

export function getFunnelDataKeysPerLine(
  chartData: IParsedChartDataFunnel,
  chartSettings: IChartSettings,
  visibleLines: IChartLine[],
  measureMode: MeasureMode,
): IFunnelDataKeysPerLine {
  if (visibleLines.length === 0) {
    return {};
  }

  if (
    chartData.mode === FunnelModeKey.TimeToConvert ||
    chartData.mode === FunnelModeKey.Frequency
  ) {
    return staticDataKeys(visibleLines);
  } else if (chartData.mode === FunnelModeKey.Conversion) {
    return comparativeDataKeys(
      chartData,
      chartSettings,
      visibleLines,
      measureMode,
    );
  }

  return {};
}
